import { useMutation, useQuery } from "@apollo/client";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    styled,
    Typography,
} from "@mui/material";
import React from "react";
import { Field, FieldRenderProps, Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";

import { pages } from "../pages/pages";
import { Category } from "../types";
import { deleteImage } from "../utils/deleteImage";
import { uploadImage } from "../utils/uploadImage";
import { createExerciseMutation, exerciseQuery, updateExerciseMutation } from "./ExerciseForm.gql";

interface FormValues {
    nameDe: string;
    nameEn: string;
    category: Category;
    descriptionDe: string;
    descriptionEn: string;
    image: string;
    imageUpload?: File;
    discreet: boolean;
    equipmentDe?: string;
    equipmentEn?: string;
    recommendationDe?: string;
    recommendationEn?: string;
    source?: string;
}

const useSubmitMutation = (navigate: NavigateFunction, id?: number) => {
    const [create] = useMutation(createExerciseMutation);
    const [update] = useMutation(updateExerciseMutation);

    if (id) {
        return async ({
            equipmentDe,
            equipmentEn,
            discreet,
            imageUpload,
            image,
            recommendationDe,
            recommendationEn,
            source,
            ...values
        }: FormValues) => {
            if (!imageUpload && !image) return;

            if (imageUpload && image) {
                await deleteImage(image);
            }

            const imageUrl = imageUpload ? await uploadImage(imageUpload, imageUpload.name) : image;

            return update({
                variables: {
                    id,
                    data: {
                        ...values,
                        equipmentDe: equipmentDe ?? "",
                        equipmentEn: equipmentEn ?? "",
                        recommendationDe: recommendationDe ?? "",
                        recommendationEn: recommendationEn ?? "",
                        source: source ?? "",
                        discreet: !!discreet,
                        image: imageUrl,
                    },
                },
                refetchQueries: ["GetExercises", "GetExercise"],
            }).then(() => {
                navigate(-1);
            });
        };
    }

    return async ({ equipmentDe, equipmentEn, discreet, imageUpload, image, recommendationDe, recommendationEn, source, ...values }: FormValues) => {
        if (!imageUpload && !image) return;

        if (imageUpload && image) {
            await deleteImage(image);
        }

        const imageUrl = imageUpload ? await uploadImage(imageUpload, imageUpload.name) : image;

        return create({
            variables: {
                data: {
                    ...values,
                    equipmentDe: equipmentDe ?? "",
                    equipmentEn: equipmentEn ?? "",
                    recommendationDe: recommendationDe ?? "",
                    recommendationEn: recommendationEn ?? "",
                    source: source ?? "",
                    discreet: !!discreet,
                    image: imageUrl,
                },
            },
            refetchQueries: ["GetExercises"],
        }).then(() => {
            navigate(-1);
        });
    };
};

const ExerciseForm: React.FunctionComponent = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = React.useState(sessionStorage.getItem("password") ? false : true);
    const [fieldValue, setFieldValue] = React.useState("");
    const [showPassword, setShowPassword] = React.useState(false);

    const handleEnter = () => {
        if (fieldValue === "ExercisingIsFun") {
            setDialogOpen(false);
            sessionStorage.setItem("password", fieldValue);
        } else {
            navigate(pages.home);
        }
    };

    const exerciseId = parseInt(id ?? "");
    const submit = useSubmitMutation(navigate, exerciseId);

    const { data, loading } = useQuery(exerciseQuery, { variables: { id: exerciseId }, skip: !exerciseId });

    if (loading) {
        return <CircularProgress />;
    }

    const handleShowPassword = () => {
        setShowPassword(true);
    };

    const handleHidePassword = () => {
        setShowPassword;
    };

    const {
        category,
        descriptionDe,
        descriptionEn,
        equipmentDe,
        equipmentEn,
        image,
        nameDe,
        nameEn,
        discreet,
        recommendationDe,
        recommendationEn,
        source,
    } = data?.exercise ?? {
        category: Category.Endurance,
    };

    const initialValues = {
        category,
        descriptionDe,
        descriptionEn,
        equipmentDe,
        equipmentEn,
        image,
        nameDe,
        nameEn,
        discreet,
        recommendationDe,
        recommendationEn,
        source,
    };

    const allCategories = (Object.values(Category) as Array<Category>).filter((x) => x !== Category.Ai);

    return (
        <Container>
            <Dialog open={dialogOpen} maxWidth="sm">
                <DialogTitle>{t("password")}</DialogTitle>
                <DialogContent style={{ padding: 20 }}>
                    <DialogContentText style={{ marginBottom: 20 }}>{t("insert_password")}</DialogContentText>
                    <InputLabel>{t("password")}</InputLabel>
                    <Input
                        autoFocus
                        margin="dense"
                        value={fieldValue}
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        onChange={(e) => {
                            setFieldValue(e.target.value);
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={handleShowPassword} onMouseDown={handleHidePassword} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            navigate(pages.home);
                        }}
                    >
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleEnter}>Ok</Button>
                </DialogActions>
            </Dialog>
            <Typography variant="h4" color="primary" gutterBottom>
                {exerciseId ? t("edit_exercise") : t("add_exercise")}
            </Typography>
            <Form
                onSubmit={submit}
                initialValues={initialValues}
                render={({ handleSubmit, pristine, invalid, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        <Label>{t("form_exercise_name_de")}</Label>
                        <Field name="nameDe" component="input" required />
                        <Label>{t("form_exercise_name_en")}</Label>
                        <Field name="nameEn" component="input" required />
                        <Label>{t("form_exercise_description_de")}</Label>
                        <Field name="descriptionDe" component="textarea" required style={{ width: "100%", height: 150 }} />
                        <Label>{t("form_exercise_description_en")}</Label>
                        <Field name="descriptionEn" component="textarea" required style={{ width: "100%", height: 150 }} />
                        <Label>{t("form_exercise_category")}</Label>
                        <Field name="category" component="select" required>
                            {allCategories.map((category) => (
                                <option key={category} value={category}>
                                    {t(category)}
                                </option>
                            ))}
                        </Field>

                        <Label>{t("form_exercise_image")}</Label>
                        {initialValues.image && (
                            <div>
                                {initialValues.image.includes("mp4") ? (
                                    <video src={initialValues.image} controls playsInline />
                                ) : (
                                    <StyledImg src={initialValues.image} alt="" />
                                )}
                            </div>
                        )}
                        <Field type="file" name="imageUpload" render={({ input, meta }) => <FileInput input={input} meta={meta} />} fullWidth />
                        <Label>{t("form_exercise_source")}</Label>
                        <Field name="source" component="input" />
                        <Label>{t("form_exercise_discreet")}</Label>
                        <Field name="discreet" type="checkbox" component="input" />
                        <Label>{t("form_exercise_recommendation_de")}</Label>
                        <Field name="recommendationDe" component="input" />
                        <Label>{t("form_exercise_recommendation_en")}</Label>
                        <Field name="recommendationEn" component="input" />
                        <Label>{t("form_exercise_equipment_de")}</Label>
                        <Field name="equipmentDe" component="input" />
                        <Label>{t("form_exercise_equipment_en")}</Label>
                        <Field name="equipmentEn" component="input" />
                        <ButtonContainer>
                            {submitting ? (
                                <CircularProgress />
                            ) : (
                                <Button type="submit" variant="contained" disabled={pristine || invalid}>
                                    {t("save")}
                                </Button>
                            )}
                        </ButtonContainer>
                    </form>
                )}
            />
        </Container>
    );
};

export default ExerciseForm;

const FileInput: React.FC<FieldRenderProps<string, HTMLElement>> = ({ input: { onChange } }) => {
    const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        if (target.files && target.files[0]) {
            onChange(target.files[0]);
        }
    };

    return <input type="file" accept="video/*,image/*" onChange={handleChange} />;
};

export const Container = styled("div")({
    padding: 20,
});

export const ButtonContainer = styled("div")({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    margin: "20px 0px",
});

export const Label = styled("div")({
    fontWeight: 700,
    marginBottom: 10,
    marginTop: 10,
    display: "flex",
});

export const StyledImg = styled("img")`
    display: flex;
    width: 40%;
    margin: 20px 0;
`;
